import { useCallback, useRef } from 'react'
import axios, { AxiosRequestConfig } from 'axios'
import { getContentTypeFromFile, getDate } from '@/utils'
import { partialUpdateBatchImport } from '@/api'
import * as Sentry from '@sentry/browser'
import { defaultConfig } from '@/config'

const clientApiUrl = defaultConfig().clientApiUrl ?? 'api.nabu-dev.io'

export type UploadEvent =
  | 'started'
  | 'retrying'
  | 'progress'
  | 'completed'
  | 'error'
  | 'aborted'

export interface UploadOptions {
  file: File
  signedUrl?: string | null
  batchImportId?: string | null
  /** Un fichier ajouté à un dossier existant : son échec ne marque pas tout le dossier `uploadFailedAt`. */
  addition?: boolean
  /** Le type déclaré à la création du fichier, à renvoyer tel quel ; sinon celui du navigateur. */
  contentType?: string
}

const MAX_RETRIES = 3
const FAIL_EVENTS = ['aborted', 'error']

const redactAwsSig = (url: string) => {
  const keysToRedact = ['X-Amz-Signature','X-Amz-Credential','X-Amz-Security-Token','X-Amz-Date','X-Amz-Algorithm','X-Amz-Expires']
  try {
    const u = new URL(url)
    keysToRedact.forEach((k: string) => u.searchParams.has(k) && u.searchParams.set(k, 'REDACTED'))
    return u.toString()
  } catch {
    return url
  }
}

function mapToUploadsDomain(presignedUrl: string, zipUpload?: boolean) {
  const uploadHost = `uploads${zipUpload ? 'd' : ''}.${clientApiUrl.split('.').slice(-2).join('.')}`; // e.g. uploads.nabu-dev.io
  // Replace scheme+host only; preserve path+query EXACTLY
  return presignedUrl.replace(/^https:\/\/[^/]+/, `https://${uploadHost}`);
}

// Promise-based sleep
const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

// Exponential backoff with *full jitter* (AWS-style)
function backoffMs(attempt: number, base = 500, cap = 10000) {
  // attempt is 1-based grows: 0.5s, 1s, 2s, 4s, ... capped
  const exp = Math.min(cap, base * 2 ** (attempt - 1))
  return Math.floor(Math.random() * exp) // full jitter
}

async function putToS3WithSentry({
  signedUrl,
  file,
  attempt,
  onProgress,
  controller,
  useZip,
  contentType,
}: {
  signedUrl: string
  file: File
  attempt: number
  onProgress?: (pct:number)=>void
  controller: AbortController
  useZip?: boolean
  contentType: string
}) {
  const started = performance.now()
  const redactedUrl = redactAwsSig(signedUrl)
  let lastPct = 0

  const config: AxiosRequestConfig = {
    headers: {
      'Content-Type': contentType,
    },
    validateStatus: () => true,
    responseType: 'text',       // so we can read S3 XML error bodies
    withCredentials: false,     // IMPORTANT for cross-origin S3
    signal: controller.signal,
    onUploadProgress: e => {
      if (e.total) {
        const pct = Math.round((e.loaded / e.total) * 100)
        if (pct !== lastPct) {
          lastPct = pct
          onProgress?.(pct)
        }
      }
    },
    transitional: { clarifyTimeoutError: true },
    timeout: 90000, // tag true timeouts as ECONNABORTED
  }

  try {
    // NOTE - if last attempt, try nabu-*.io domain instead
    const finalSignedUrl = attempt === MAX_RETRIES
      ? mapToUploadsDomain(signedUrl, useZip)
      : signedUrl
    const res = await axios.put(finalSignedUrl, file, config)
    const durationMs = Math.round(performance.now() - started)

    if (res.status < 200 || res.status >= 300) {
      // S3 usually returns XML headers include request IDs if CORS allows.
      const reqId = res.headers?.['x-amz-request-id']
      const id2   = res.headers?.['x-amz-id-2']
      const etag  = res.headers?.etag

      const err: any = new Error(`S3 upload failed: ${res.status} ${res.statusText}`)
      err.httpStatus = res.status
      const ra = res.headers?.['retry-after']
      err.retryAfterMs = ra ? Number(ra) * 1000 : undefined
      // @ts-ignore
      Sentry?.captureException?.(err, {
        tags: { area: 's3-upload', httpStatus: String(res.status) },
        extra: {
          attempt,
          url: redactedUrl,
          method: 'PUT',
          durationMs,
          file: { name: file.name, size: file.size, type: file.type },
          amzRequestId: reqId,
          amzId2: id2,
          etag,
          responseHeaders: res.headers,
          responseBody: String(res.data).slice(0, 2000),
        },
      })
      throw err
    }

    // success breadcrumb
    // @ts-ignore
    Sentry?.addBreadcrumb?.({
      category: 's3-upload',
      level: 'info',
      data: {
        attempt,
        url: redactedUrl,
        etag: res.headers?.etag,
        durationMs,
      },
    })

    return res
  } catch (err: any) {
    const durationMs = Math.round(performance.now() - started)
    const isAxios = !!axios.isAxiosError?.(err)
    const code = isAxios ? err.code : undefined

    // Network-level: no response (CORS/proxy/timeout/abort)
    const resp = isAxios ? err.response : undefined
    const req  = isAxios ? err.request  : undefined // XHR instance in browser

    // @ts-ignore
    Sentry?.captureException?.(err, {
      tags: { area: 's3-upload', kind: code || (err.name === 'AbortError' ? 'abort' : 'network') },
      extra: {
        attempt,
        url: redactedUrl,
        durationMs,
        file: { name: file.name, size: file.size, type: file.type },
        axios: {
          code,
          message: err.message,
          isAxiosError: isAxios,
          readyState: req?.readyState,
          xhrStatus: req?.status,
          xhrResponseType: req?.responseType,
          hasResponse: !!resp,
          responseStatus: resp?.status,
          responseHeaders: resp?.headers,
          responseData: String(resp?.data || '').slice(0, 2000),
        },
      },
    })
    throw err
  }
}

export function useUploadFile(useZip = false) {
  const abortControllerRef = useRef<AbortController | null>(null)
  const onEvent = (
    file: File,
    batchImportId: string,
    event: UploadEvent,
    detail?: any,
    addition = false
  ) => {
    const currentDate = getDate()
    if (FAIL_EVENTS.includes(event) && !addition) {
      void partialUpdateBatchImport(batchImportId, {
        // eslint-disable-next-line
        // @ts-ignore
        uploadFailedAt: currentDate.toISOString(),
        // eslint-disable-next-line
        // @ts-ignore
        metadata: { ...detail, file: file.name }, // eslint-disable-line
      })
    }
  }

  const uploadFile = useCallback(async ({ file, signedUrl, batchImportId, addition = false, contentType: declaredType }: UploadOptions): Promise<void> => {
    const contentType = declaredType ?? getContentTypeFromFile(file)
    if (!signedUrl || !batchImportId || !contentType) {
      const e = new Error('signedUrl, contentType and batchImportId are required')
      Sentry.captureException(e)
      return Promise.reject(e)
    }

    const onProgress = (file: File, pct: number) => {
      console.info('progress for file ' + file.name, pct)
      // Emit progress event
      // @ts-ignore
      onEvent?.(file, batchImportId!, 'progress', { percent: pct })
    }

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // lifecycle event
      onEvent?.(file, batchImportId, attempt === 1 ? 'started' : 'retrying', { attempt })

      const controller = new AbortController()
      abortControllerRef.current = controller

      try {
        await putToS3WithSentry({
          signedUrl,
          file,
          attempt,
          onProgress: (pct) => onProgress(file, pct),
          controller,
          useZip,
          contentType,
        })

        onEvent?.(file, batchImportId, 'completed')
        abortControllerRef.current = null
        return
      } catch (err) {

        if (axios.isCancel(err) || (axios.isAxiosError(err) && err.code === 'ERR_CANCELED')) {
          onEvent?.(file, batchImportId, 'aborted', undefined, addition)
          throw err
        }

        const status = (err as any)?.httpStatus
        const retryAfterMs = (err as any)?.retryAfterMs
        const shouldRetry = axios.isAxiosError(err)
          ? (!status || status >= 500 || status === 429)
          : true

        if (!shouldRetry || attempt === MAX_RETRIES) {
          onEvent?.(file, batchImportId, 'error', { error: err }, addition)
          throw err
        }

        const delay = retryAfterMs ?? backoffMs(attempt)
        // See retries in Sentry timelines
        Sentry.addBreadcrumb({ category: 's3-upload', level: 'info', message: `retrying in ${delay}ms`, data: { attempt } })
        // If user aborted during backoff, bail out
        if (abortControllerRef.current?.signal.aborted) throw err
        await sleep(delay)
      } finally {
        abortControllerRef.current = null // ensure cleanup on all paths
      }
    }
  }, [])

  const abortUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
  }

  return { uploadFile, abortUpload }
}
